import React from "react";

const CropSummaryCards = ({ combinedData, cropLines = [] }) => {

  const generateCropColor = (index) => {
    const hue = (index * 137.5) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  const calculateTotalWaterRequirement = (cropResult) => {
    if (!cropResult.growth_stage_calculations) {
      return {
        total_et: 0,
        total_water_liters: 0,
        average_daily_et: 0,
        average_daily_water_liters: 0,
        found: false,
      };
    }

    const gsc = cropResult.growth_stage_calculations;

    return {
      total_et: gsc.total_et || 0,
      total_water_liters: gsc.total_water_requirement_liters || 0,
      average_daily_et: gsc.average_daily_et || 0,
      average_daily_water_liters:
        gsc.average_daily_water_requirement_liters || 0,
      total_growing_days: gsc.total_growing_days || 0,
      found: true,
    };
  };

  const formatWaterRequirement = (waterData, area) => {
    if (!waterData.found || waterData.total_et === 0) {
      return {
        display: "N/A",
        totalDisplay: "N/A",
        debug: "No water data found",
      };
    }

    const totalWaterLiters = waterData.total_water_liters;
    const totalWaterForArea = totalWaterLiters * area;

    const totalET = waterData.total_et;
    let etDisplay;
    if (totalET > 1000) {
      etDisplay = `${(totalET / 1000).toFixed(1)}m ET`;
    } else {
      etDisplay = `${totalET.toFixed(0)}mm ET`;
    }

    const waterDisplay = `${totalWaterLiters.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} L`;

    const totalDisplay = `${totalWaterForArea.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} L`;

    return {
      display: waterDisplay,
      etDisplay: etDisplay,
      totalDisplay: totalDisplay,
      totalWaterForArea: totalWaterForArea,
      debug: `${
        waterData.total_growing_days
      } days, ${waterData.average_daily_et.toFixed(1)}mm/day avg`,
    };
  };

  if (!combinedData || !combinedData.crops || combinedData.crops.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "var(--space-8)",
          color: "var(--color-neutral-500)",
          backgroundColor: "var(--color-neutral-50)",
          borderRadius: "var(--radius-lg)",
          border: `2px dashed var(--color-neutral-300)`,
        }}
      >
        <div style={{ fontSize: "var(--font-size-3xl)", marginBottom: "var(--space-2)" }}>🌱</div>
        <p style={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-medium)" }}>
          No crop data available for summary
        </p>
      </div>
    );
  }

  const summaryData =
    cropLines.length > 0
      ? cropLines.map((line, index) => {
          const cropResult = combinedData.crops.find(
            (crop) => crop.crop_input.crop_type === line.dataKey
          );
          const waterData = cropResult
            ? calculateTotalWaterRequirement(cropResult)
            : null;

          return {
            ...line,
            waterData: waterData,
            cropResult: cropResult,
          };
        })
      : combinedData.crops.map((cropResult, index) => {
          const waterData = calculateTotalWaterRequirement(cropResult);

          return {
            name:
              cropResult.crop_input.crop_type.charAt(0).toUpperCase() +
              cropResult.crop_input.crop_type.slice(1),
            color: generateCropColor(index),
            area: cropResult.crop_input.area,
            plantingDate: cropResult.crop_input.planting_date,
            totalDays: cropResult.growth_stage_calculations
              ? cropResult.growth_stage_calculations.total_growing_days || 0
              : 0,
            dataKey: cropResult.crop_input.crop_type,
            waterData: waterData,
            cropResult: cropResult,
          };
        });

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "var(--space-4)",
        }}
      >
        {summaryData.map((crop, index) => {
          const waterFormatted = formatWaterRequirement(
            crop.waterData,
            crop.area
          );

          return (
            <div
              key={crop.dataKey || index}
              className="card iwmi-card-water"
              style={{
                padding: "var(--space-5)",
                backgroundColor: "#ffffff",
                border: `var(--card-border-width) solid var(--card-border-color)`,
                borderRadius: "var(--radius-lg)",
                boxShadow: "var(--shadow-sm)",
                transition: "all var(--transition-base)",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "var(--shadow-md)";
                e.currentTarget.style.borderColor = "var(--color-neutral-300)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                e.currentTarget.style.borderColor = "var(--card-border-color)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "var(--space-4)",
                }}
              >
                <h5
                  style={{
                    margin: "0",
                    fontWeight: "var(--font-weight-semibold)",
                    fontSize: "var(--font-size-lg)",
                    color: crop.color,
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                    lineHeight: "var(--line-height-tight)",
                  }}
                >
                  {crop.name}
                </h5>
                <span
                  style={{
                    fontSize: "var(--font-size-xs)",
                    color: "var(--color-neutral-600)",
                    backgroundColor: "var(--color-neutral-100)",
                    padding: "var(--space-1) var(--space-3)",
                    borderRadius: "var(--radius-full)",
                    fontWeight: "var(--font-weight-medium)",
                    border: `1px solid var(--color-neutral-200)`,
                  }}
                >
                  {crop.area} acres
                </span>
              </div>

              <div
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-neutral-600)",
                  lineHeight: "var(--line-height-relaxed)"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "var(--space-3)",
                    gap: "var(--space-2)",
                    padding: "var(--space-2)",
                    backgroundColor: "var(--color-neutral-50)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <span style={{ fontSize: "var(--font-size-base)" }}>📅</span>
                  <span style={{ color: "var(--color-neutral-700)" }}>
                    <strong style={{ fontWeight: "var(--font-weight-medium)" }}>Planted:</strong>{" "}
                    {new Date(crop.plantingDate).toLocaleDateString()}
                  </span>
                </div>

                {crop.totalDays > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "var(--space-3)",
                      gap: "var(--space-2)",
                      padding: "var(--space-2)",
                      backgroundColor: "var(--color-neutral-50)",
                      borderRadius: "var(--radius-md)",
                    }}
                  >
                    <span style={{ fontSize: "var(--font-size-base)" }}>⏱️</span>
                    <span style={{ color: "var(--color-neutral-700)" }}>
                      <strong style={{ fontWeight: "var(--font-weight-medium)" }}>Crop Period:</strong> {crop.totalDays} days
                    </span>
                  </div>
                )}

                {}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "var(--space-2)",
                    gap: "var(--space-2)",
                    padding: "var(--space-2)",
                    backgroundColor: "var(--color-primary-50)",
                    borderRadius: "var(--radius-md)",
                    border: `1px solid var(--color-primary-200)`,
                  }}
                >
                  <span style={{ fontSize: "var(--font-size-base)" }}>💧</span>
                  <span style={{ color: "var(--color-primary-700)", fontWeight: "var(--font-weight-medium)" }}>
                    <strong>Water Need:</strong> {waterFormatted.display}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CropSummaryCards;
